import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';
import { Layout } from '../components/Layout';
import { getCheckoutStatus } from '../services/api';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

export const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const { clearCart } = useCart();

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async (attempts = 0) => {
      const maxAttempts = 5;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('timeout');
        return;
      }

      try {
        const data = await getCheckoutStatus(sessionId);
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          clearCart();
          return;
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Continue polling
        setTimeout(() => pollStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking status:', error);
        setStatus('error');
      }
    };

    pollStatus();
  }, [sessionId, clearCart]);

  if (status === 'loading') {
    return (
      <Layout>
        <div className="section-spacing container-luxury text-center" data-testid="checkout-loading">
          <div className="max-w-md mx-auto">
            <div className="spinner mx-auto mb-6" />
            <h1 className="font-heading text-2xl text-[#2C2420] mb-4">
              Processing Your Order
            </h1>
            <p className="text-[#5D5550]">
              Please wait while we confirm your payment...
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (status === 'success') {
    return (
      <Layout>
        <div className="section-spacing container-luxury" data-testid="checkout-success">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-lg mx-auto text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring' }}
            >
              <CheckCircle className="h-20 w-20 text-[#2F855A] mx-auto mb-6" />
            </motion.div>

            <h1 className="font-heading text-3xl md:text-4xl text-[#2C2420] mb-4">
              Thank You for Your Order!
            </h1>
            <p className="text-[#5D5550] mb-8">
              Your payment was successful. We've sent a confirmation email with your order details.
            </p>

            <div className="bg-[#F2EFE9] p-6 mb-8 text-left">
              <div className="flex items-center gap-3 mb-4">
                <Package className="h-5 w-5 text-[#C6A87C]" />
                <span className="text-sm font-medium text-[#2C2420]">What's Next?</span>
              </div>
              <ul className="space-y-2 text-sm text-[#5D5550]">
                <li>• We're preparing your order for shipment</li>
                <li>• You'll receive tracking information via email</li>
                <li>• Estimated delivery: 5-10 business days worldwide</li>
              </ul>
            </div>

            {paymentData && (
              <div className="bg-[#F9F8F6] border border-[#EBE5DE] p-4 mb-8">
                <p className="text-sm text-[#8C8580]">Amount Paid</p>
                <p className="font-heading text-2xl text-[#2C2420]">
                  €{paymentData.amount_total.toFixed(2)}
                </p>
              </div>
            )}

            <Link to="/shop">
              <Button className="btn-primary" data-testid="continue-shopping-success">
                Continue Shopping
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </Layout>
    );
  }

  // Error states
  return (
    <Layout>
      <div className="section-spacing container-luxury text-center" data-testid="checkout-error">
        <div className="max-w-md mx-auto">
          <h1 className="font-heading text-2xl text-[#2C2420] mb-4">
            {status === 'expired' ? 'Session Expired' : 'Something Went Wrong'}
          </h1>
          <p className="text-[#5D5550] mb-8">
            {status === 'expired'
              ? 'Your payment session has expired. Please try again.'
              : 'We couldn\'t process your payment. Please try again or contact support.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/cart">
              <Button className="btn-primary">Return to Cart</Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="btn-secondary">
                Contact Support
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
};
